import { EwayBillPanel } from "./EwayBill";
import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Clock3, Gavel, ShieldCheck, Sparkles, X, Lock, MapPin, CheckCircle2, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { API, money, statusLabel, useCountdown, Countdown, qtyLabel, PerUnit } from "./shared";
import { ChatPanel } from "./ChatPanel";
import { CapabilityMatchPanel } from "./SupplierAnalytics";
import { BuyerToolkit, SupplierWonPanel, InvoicesPanel } from "./BuyerToolkit";
import { AdminGstReview } from "./GstClass";
import { AdminJobActions } from "./JobAdminActions";
import { NoBidPanel } from "./NoBidAuctions";
import { ArtworkPreviewList } from "./ArtworkPreview";
import { AdminBidEditor } from "./AdminBidEditor";

// ============================================================
// JOB DRAWER (right side detail)
// ============================================================
// ============================================================
// BUYER · Attach press-ready artwork to an existing job
// ============================================================
// Fills the gap that caused Shivani's missing-artwork bug. Buyer can upload
// press-ready files AFTER job creation (pre-award, post-award, up to dispatch)
// without re-posting the job. Attaching after award triggers an
// `artwork_released` inbox notification to the supplier so pre-press unblocks.
export function BuyerArtworkUploader({ job, refresh }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attached, setAttached] = useState(null);
  const existing = attached || job.press_ready_assets || [];
  const empty = existing.length === 0;
  const release = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/jobs/${job.id}/artwork`, {
        file_ids: files.map(f => f.id).filter(Boolean),
        replace: false,
      });
      setAttached(r.data.press_ready_assets || []);
      toast.success(
        job.status === "awarded" || job.status === "dispatched"
          ? "Artwork released to supplier — pre-press is now unblocked."
          : "Artwork attached · will auto-release to the winning supplier on award.",
      );
      setFiles([]);
      setExpanded(false);
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach artwork.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="buyer-artwork-uploader"
      style={{
        margin: "16px 0 6px",
        padding: "14px 16px",
        borderRadius: 10,
        border: empty ? "1px solid #FDE68A" : "1px solid #D1FAE5",
        background: empty ? "#FFFBEB" : "#F0FDF4",
      }}
    >
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          {empty ? <AlertTriangle size={16} style={{color:"#92400E"}}/> : <CheckCircle2 size={16} style={{color:"#065F46"}}/>}
          <b style={{fontSize:13, color: empty ? "#92400E" : "#065F46"}}>
            {empty ? "No press-ready artwork attached yet" : `${existing.length} press-ready file${existing.length === 1 ? "" : "s"} attached`}
          </b>
        </div>
        {!expanded && (
          <button
            data-testid="buyer-artwork-open"
            onClick={() => setExpanded(true)}
            style={{
              background: empty ? "#92400E" : "transparent",
              color: empty ? "#FEF3C7" : "#065F46",
              border: empty ? "none" : "1px solid #A7F3D0",
              padding: "6px 14px", borderRadius: 999,
              fontSize: 11, fontWeight: 800, cursor: "pointer",
            }}
          >{empty ? "Upload artwork" : "+ Add more"}</button>
        )}
      </div>
      {existing.length > 0 && !expanded && (
        <ul style={{margin:"8px 0 0 24px", padding:0, fontSize:12, color:"#065F46", lineHeight:1.6}}>
          {existing.slice(0, 4).map(a => (
            <li key={a.file_id || a.url}>
              <a href={a.url} target="_blank" rel="noreferrer" style={{color:"#065F46"}}>{a.name}</a>
              <span style={{color:"#6B7280"}}> · {a.size}</span>
            </li>
          ))}
          {existing.length > 4 && <li style={{color:"#6B7280"}}>+ {existing.length - 4} more</li>}
        </ul>
      )}
      {expanded && (
        <div style={{marginTop:12}}>
          <FileDropzone
            testid="buyer-artwork-dropzone"
            scope="artwork"
            jobId={job.id}
            accept="application/pdf,image/*,.dxf,.ai,.eps,.zip,.tiff,.tif"
            value={files}
            onChange={setFiles}
            label="Drop press-ready PDFs, dielines or source files"
            hint="Up to 100 MB each · preflighted locally · only the awarded supplier can download these."
          />
          <div style={{display:"flex", justifyContent:"flex-end", gap:8, marginTop:10}}>
            <button
              data-testid="buyer-artwork-cancel"
              onClick={() => { setExpanded(false); setFiles([]); }}
              disabled={busy}
              style={{background:"transparent", border:"1px solid #D1D5DB", color:"#374151", padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer"}}
            >Cancel</button>
            <button
              data-testid="buyer-artwork-release"
              onClick={release}
              disabled={busy || files.length === 0}
              style={{
                background: files.length && !busy ? "#059669" : "#9CA3AF",
                color: "#fff", border: "none",
                padding: "7px 18px", borderRadius: 8,
                fontSize: 12, fontWeight: 800,
                cursor: files.length && !busy ? "pointer" : "not-allowed",
              }}
            >
              {busy ? "Releasing…" :
                job.status === "awarded" || job.status === "dispatched"
                  ? `Release ${files.length || ""} to supplier`
                  : `Attach ${files.length || ""} to job`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function JobDrawer({ job, bids, role, supplierId, close, refresh, setModal, selectedBidId, setSelectedBidId, reloadBids }) {
  const isBrief = !job.category;
  return (
    <div className="drawer-backdrop" onClick={close}>
      <aside className="drawer" onClick={e => e.stopPropagation()}>
        <button data-testid="drawer-close" className="close-btn" onClick={close}><X size={18}/></button>
        <div className="drawer-top">
          <p className="eyebrow">{isBrief ? "ARTWORK BRIEF" : "JOB DETAIL"}</p>
          <h2>{job.title}</h2>
          <div className="drawer-status-row">
            <span className={`status ${job.status}`}>{statusLabel[job.status] || job.status}</span>
            {["live", "closed"].includes(job.status) && job.auction_ends_at && (
              <Countdown endsAt={job.auction_ends_at} size="lg" onExpire={refresh}/>
            )}
            {job.is_disputed && <span className="status disputed" data-testid="disputed-badge"><AlertTriangle size={11}/> Disputed</span>}
          </div>
        </div>

        {job.is_disputed && (
          <div className="drawer-callout danger" data-testid="dispute-banner">
            <AlertTriangle size={17}/>
            <div>
              <b>Dispute open — {job.dispute_reason}</b>
              <span>Funds frozen in escrow while admin reviews. Both parties can add evidence in the Disputes tab.</span>
            </div>
          </div>
        )}

        {isBrief ? (
          <BriefDetail brief={job} refresh={refresh} close={close}/>
        ) : (
          <>
            <div className="spec-grid">
              <div><label>Quantity</label><b>{qtyLabel(job)}</b></div>
              <div><label>Ship to</label><b data-testid="job-ship-to">{job.ship_to}{job.delivery_pincode ? ` · PIN ${job.delivery_pincode}` : ""}</b></div>
              {job.need_by_date && <div><label>Need by</label><b data-testid="job-need-by">{new Date(job.need_by_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</b></div>}
              <div><label>{job.category !== "Procurement" ? "Stock" : String(job.gst_class).startsWith("svc_") ? "Scope" : "Specification"}</label><b data-testid="job-spec-stock">{job.stock}</b></div>
              {job.dimensions && <div><label>{job.category === "Procurement" ? "Size / format" : "Dimensions"}</label><b data-testid="job-spec-dimensions">{job.dimensions}</b></div>}
              {job.finishes && job.finishes !== "None" && <div><label>{job.category === "Procurement" ? "Preferences" : "Finishes"}</label><b>{job.finishes}</b></div>}
              {job.price_floor > 0 && <div><label>Price floor</label><b>{money(job.price_floor)}</b></div>}
              {(job.capability_tags || []).length > 0 && <div><label>Capabilities</label><b>{job.capability_tags.join(", ")}</b></div>}
              {job.required_capabilities?.press_type && <div><label>Required press</label><b>{job.required_capabilities.press_type}</b></div>}
              {job.required_capabilities?.product_type && <div><label>Product type</label><b>{job.required_capabilities.product_type}</b></div>}
              {job.required_capabilities?.substrate && <div><label>Required substrate</label><b>{job.required_capabilities.substrate}</b></div>}
              {(job.required_capabilities?.post_press || []).length > 0 && <div><label>Required finishes</label><b>{job.required_capabilities.post_press.join(", ")}</b></div>}
              {job.required_capabilities?.max_distance_km ? <div><label>Max distance</label><b>{job.required_capabilities.max_distance_km} km</b></div> : null}
            </div>

            {role === "buyer" && job.artwork_required !== false && ["live","pending_review","awarded","dispatched"].includes(job.status) && (
              <BuyerArtworkUploader job={job} refresh={refresh}/>
            )}

            {/* The 5-pillar MATCH panel (shows suppliers + their scores) is
                hidden during the admin review stage per product spec — admins
                shouldn't be biased by the matching suppliers while deciding
                whether to approve the job. The "5-Pillar Capability
                REQUIREMENTS" callout (above) stays visible so admin can audit
                what the buyer asked for. Match panel reappears once the job
                is live/closed so admin can curate the invite list. Buyers
                never see the panel either (sealed-bid anonymity). */}
            {role === "admin" && ["live", "closed"].includes(job.status) && job.required_capabilities && (
              <CapabilityMatchPanel jobId={job.id}/>
            )}

            {["printer", "material_supplier", "finishing_partner", "admin"].includes(role) && (job.press_ready_assets || []).length > 0 && <ArtworkPreviewList jobId={job.id}/>}

            {role === "printer" && job.capability_match && (
              <div className="drawer-callout" data-testid="own-match-badge">
                <ShieldCheck size={17}/>
                <div>
                  <b>Your 5-pillar match: {job.capability_match.total_score}%</b>
                  <span>{Object.entries(job.capability_match.pillars).map(([k, v]) => `${k.replace(/_/g, " ")} ${v.score}/${v.max}`).join(" · ")}</span>
                </div>
              </div>
            )}

            {role === "admin" && job.gst_class && <AdminGstReview job={job} refresh={refresh}/>}
            {role === "admin" && job.status === "pending_review" && <AdminReview job={job} refresh={refresh} close={close}/>}
            {role === "admin" && ["pending_review", "live", "closed"].includes(job.status) && !job.awarded_supplier_id && <AdminJobActions job={job} bids={bids} refresh={refresh} close={close}/>}
            <NoBidPanel job={job} bids={bids} role={role} refresh={refresh} close={close}/>

            {role === "buyer" && ["awarded", "dispatched", "completed"].includes(job.status) && (
              <BuyerToolkit job={job} refresh={async () => { await refresh(); await reloadBids(); }} setModal={setModal}/>
            )}

            {["live", "closed"].includes(job.status) && (
              <LiveAuctionPanel
                job={job}
                bids={bids}
                role={role}
                selectedBidId={selectedBidId}
                setSelectedBidId={setSelectedBidId}
                setModal={setModal}
                reloadBids={reloadBids}
                refresh={refresh}
              />
            )}

            {(role === "admin" || (role === "printer" && job.awarded_supplier_id === supplierId)) && ["awarded", "dispatched", "completed"].includes(job.status) && !isBrief && (
              <EwayBillPanel job={job} role={role} onChange={refresh}/>
            )}
            {role === "printer" && ["awarded", "dispatched", "completed"].includes(job.status) && job.awarded_supplier_id === supplierId && (
              <SupplierWonPanel job={job} setModal={setModal}/>
            )}

            {!isBrief && <InvoicesPanel job={job} role={role}/>}

            {!isBrief && job.awarded_supplier_id && (
              <ChatPanel scopeType="job" scopeId={job.id} role={role}/>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

export function LiveAuctionPanel({ job, bids, role, selectedBidId, setSelectedBidId, setModal, reloadBids, refresh }) {
  const remainingMs = useCountdown(job.auction_ends_at);
  const expired = job.status === "closed" || (remainingMs !== null && remainingMs <= 0);
  const canBid = role === "printer" && !expired;
  const editLocked = remainingMs !== null && remainingMs < 15 * 60 * 1000;
  const [withdrawing, setWithdrawing] = useState(false);
  const withdraw = async () => {
    if (!window.confirm("Withdraw your sealed bid? You can bid again while the auction is live.")) return;
    setWithdrawing(true);
    try { await axios.delete(`${API}/jobs/${job.id}/bids/mine`); toast.success("Bid withdrawn"); await reloadBids?.(); await refresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not withdraw"); } finally { setWithdrawing(false); }
  };
  const canAward = role === "buyer" && bids.length > 0;
  const selectedBelowFloor = !!bids.find(b => b.id === selectedBidId)?.below_floor;

  return (
    <>
      <div className="drawer-section-title">
        <h3>{role === "printer" ? "Your sealed bid" : "Sealed bids"}</h3>
        <span>{role === "buyer" ? `${bids.length} anonymous offers` : role === "printer" ? "One sealed offer · edit or withdraw until 15 min before close" : `${bids.length} bids received`}</span>
      </div>
      {expired ? (
        <div className="drawer-callout" data-testid="auction-closed-callout">
          <Clock3 size={17}/>
          <div>
            <b>Auction window has closed</b>
            <span>{!bids.length ? (job.no_bid_closed && role !== "printer" ? "No bids were received. See the tips above to relist." : "No bids were received.") : role === "buyer" ? "Review the sealed offers and award to the winning supplier." : "Bidding is no longer accepted. The buyer will award shortly."}</span>
          </div>
        </div>
      ) : role === "buyer" ? (
        <div className="drawer-callout" style={{flexDirection:"column",alignItems:"stretch",gap:6}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <Lock size={17}/>
            <div>
              <b>Anonymous bidding — identity stays sealed end-to-end</b>
              <span>You'll never see supplier contact details on GoPrintPack — all coordination (chat, artwork handoff, proofs, delivery) stays anonymous through this workspace. You pick the winning bid using <b>Price 50% · Performance 30% · Quality 20%</b> and the supplier's verified rating.</span>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8,paddingTop:8,borderTop:"1px dashed #E2E8F0"}}>
            <div data-testid="anon-faq-what-stays-hidden" style={{fontSize:11,color:"#334155",lineHeight:1.55}}>
              <b style={{display:"block",marginBottom:3,color:"#0F172A"}}>What stays anonymous</b>
              ✔ No company name<br/>
              ✔ No phone or email<br/>
              ✔ No GSTIN<br/>
              ✔ No location below city-level
            </div>
            <div data-testid="anon-faq-escrow" style={{fontSize:11,color:"#334155",lineHeight:1.55}}>
              <b style={{display:"block",marginBottom:3,color:"#0F172A"}}>Your escrow is safe</b>
              Your payment is held by <b>Razorpay</b> (RBI PA/PG regulated) and released only when you confirm delivery, or 7 days after dispatch — not before.
            </div>
          </div>
        </div>
      ) : null}
      <div className="bid-list">
        {bids.length === 0 && canBid && (
          <button data-testid="place-bid-button" className="primary-btn full" onClick={() => setModal("bid")}>
            Submit sealed bid <Gavel size={16}/>
          </button>
        )}
        {bids.length === 0 && role === "printer" && expired && (
          <div className="empty" data-testid="printer-no-bid-empty">
            <ClipboardCheck size={22}/><span>You didn't submit a sealed bid on this auction.</span>
          </div>
        )}
        {bids.map((b, i) => (
          <BidCard
            key={b.id}
            bid={b}
            job={job}
            index={i}
            role={role}
            selected={selectedBidId === b.id}
            onSelect={() => role === "buyer" && setSelectedBidId(b.id)}
            onChanged={async () => { await reloadBids?.(); await refresh?.(); }}
          />
        ))}
        {role === "printer" && canBid && job.status === "live" && bids.length > 0 && (
          <div data-testid="my-bid-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
            {editLocked ? <span data-testid="bid-edit-locked" style={{ fontSize: 12, color: "#B45309" }}>Bids are locked in the last 15 minutes of the auction.</span> : (<>
              <button data-testid="edit-bid-button" className="so-btn so-blue" onClick={() => setModal("bid-edit")}><Pencil size={14}/>Edit bid</button>
              <button data-testid="withdraw-bid-button" className="so-btn so-red" disabled={withdrawing} onClick={withdraw}><Trash2 size={14}/>{withdrawing ? "Withdrawing…" : "Withdraw bid"}</button>
              {bids[0]?.revision_count > 0 && <span data-testid="bid-revision-count" style={{ fontSize: 11.5, color: "#64748b" }}>Revised {bids[0].revision_count}×</span>}
            </>)}
          </div>
        )}
      </div>
      {canAward && (
        <button
          data-testid="award-job-button"
          className="primary-btn full"
          disabled={!selectedBidId || selectedBelowFloor}
          onClick={() => setModal("payment")}
        >
          {!selectedBidId ? "Select an offer to award" : selectedBelowFloor ? "This offer is below the price floor" : "Award selected offer & fund escrow"} <Gavel size={16}/>
        </button>
      )}
    </>
  );
}

const BID_TAGS = {
  best_value: { label: "★ Best Value", bg: "#ECFDF5", fg: "#047857", bd: "#6EE7B7" },
  lowest_total_cost: { label: "₹ Lowest Total Cost", bg: "#EFF6FF", fg: "#1D4ED8", bd: "#BFDBFE" },
  fastest_delivery: { label: "⚡ Fastest Delivery", bg: "#FFF7ED", fg: "#C2410C", bd: "#FED7AA" },
};

const BidTags = ({ bid }) => (bid.tags || []).length > 0 && (
  <span data-testid={`bid-tags-${bid.id}`} style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>
    {bid.tags.map(t => BID_TAGS[t] && (
      <span key={t} data-testid={`bid-tag-${t}-${bid.id}`} style={{display:"inline-flex",alignItems:"center",background:BID_TAGS[t].bg,color:BID_TAGS[t].fg,border:`1px solid ${BID_TAGS[t].bd}`,padding:"1px 8px",borderRadius:999,fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>
        {BID_TAGS[t].label}
      </span>
    ))}
  </span>
);

const TRANSIT_NOTE = { printer_locked: "committed", shiprocket_live: "live courier ETA", printer_estimate: "printer's estimate" };

const NeedByBadge = ({ bid }) => bid.meets_need_by == null ? null : (
  <span data-testid={`bid-need-by-${bid.id}`} style={{display:"inline-block",marginTop:3,padding:"1px 8px",borderRadius:999,fontSize:10,fontWeight:700,
    background: bid.meets_need_by ? "#ECFDF5" : "#FEF2F2", color: bid.meets_need_by ? "#047857" : "#B91C1C", border:`1px solid ${bid.meets_need_by ? "#6EE7B7" : "#FCA5A5"}`}}>
    {bid.meets_need_by ? "✓ Arrives in time" : "⚠ Misses date"} · by {new Date(bid.arrival_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
  </span>
);

const DeliveredIn = ({ bid }) => bid.delivered_in_days ? (
  <span data-testid={`bid-delivered-in-${bid.id}`} style={{display:"block",fontSize:11,color:"#1E3A8A",fontWeight:600,marginTop:2}}>
    <span style={{whiteSpace:"nowrap"}}><Clock3 size={11} style={{verticalAlign:-1,display:"inline"}}/> Delivered in {bid.delivered_in_days} days</span>
    <span style={{color:"#64748B",fontWeight:400}}> · {bid.turnaround_days}d production + {bid.transit_days}d transit ({TRANSIT_NOTE[bid.transit_source] || "estimate"})</span>
  </span>
) : null;

const LandedCost = ({ bid, job }) => bid.total_cost_inr != null && (
  <span data-testid={`bid-landed-${bid.id}`} style={{display:"block",fontSize:11,fontWeight:500,color:"#475569",marginTop:3}}>
    {bid.estimated_delivery_inr != null && <>+ {money(bid.estimated_delivery_inr)} delivery{bid.delivery_is_estimate ? " (est.)" : " (fixed)"}<br/></>}
    Total incl. GST <b style={{color:"#0F172A"}}>{money(bid.total_cost_inr)}</b>
    <PerUnit price={bid.total_cost_inr} qty={job?.quantity} unit={job?.quantity_unit} testid={`bid-landed-per-unit-${bid.id}`} suffix="landed, all-in"/>
  </span>
);

function BidRevisionHistory({ bid, job }) {
  const [open, setOpen] = useState(false);
  const revs = bid.revisions || [];
  const first = Number(revs[0]?.price) || 0, cur = Number(bid.price) || 0;
  const dropPct = first ? ((first - cur) / first) * 100 : 0;
  const ends = job?.auction_ends_at ? new Date(job.auction_ends_at).getTime() : null;
  const lateEdit = ends && bid.revised_at && ends - new Date(bid.revised_at).getTime() < 60 * 60 * 1000;
  const flags = [revs.length >= 4 && `${revs.length} revisions`, Math.abs(dropPct) >= 25 && `${dropPct > 0 ? "-" : "+"}${Math.abs(dropPct).toFixed(0)}% vs first offer`, lateEdit && "edited in last hour"].filter(Boolean);
  const fmtT = (t) => new Date(t).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  return (
    <div data-testid={`bid-revisions-${bid.id}`} style={{ marginTop: 6 }}>
      <button data-testid={`bid-revisions-toggle-${bid.id}`} onClick={() => setOpen(!open)} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#1D4ED8" }}>
        {open ? "Hide" : "Show"} price history ({revs.length} revision{revs.length > 1 ? "s" : ""})
      </button>
      {flags.length > 0 && <span data-testid={`bid-revision-flag-${bid.id}`} style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: "#B45309", background: "#FEF3C7", borderRadius: 999, padding: "1px 7px" }}>⚠ {flags.join(" · ")}</span>}
      {open && (
        <table style={{ marginTop: 6, fontSize: 11.5, borderCollapse: "collapse" }}>
          <tbody>
            {revs.map((r, i) => (
              <tr key={i}><td style={{ padding: "2px 10px 2px 0", color: "#64748b" }}>v{i + 1} · until {fmtT(r.at)}</td><td style={{ padding: "2px 10px 2px 0" }}><b>{money(r.price)}</b></td><td style={{ color: "#64748b" }}>{r.turnaround_days}d{r.edited_by === "admin" && <span data-testid={`bid-rev-admin-${bid.id}-${i}`} style={{ marginLeft: 8, color: "#7C3AED", fontWeight: 700 }}>→ changed by admin: {r.admin_reason}</span>}</td></tr>
            ))}
            <tr><td style={{ padding: "2px 10px 2px 0", color: "#0F172A", fontWeight: 700 }}>Current · {fmtT(bid.revised_at)}</td><td style={{ padding: "2px 10px 2px 0" }}><b>{money(bid.price)}</b></td><td style={{ color: "#64748b" }}>{bid.turnaround_days}d</td></tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

const BelowFloor = ({ bid, job }) => bid.below_floor ? (
  <span data-testid={`bid-below-floor-${bid.id}`} style={{ display: "inline-block", marginTop: 3, background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA", borderRadius: 999, padding: "1px 8px", fontSize: 10.5, fontWeight: 700 }}>
    {job?.price_floor > 0 ? `Below price floor of ${money(job.price_floor)}` : "Below the minimum acceptable price"} · can't be awarded until raised
  </span>
) : null;

export function BidCard({ bid, job, index, role, selected, onSelect, onChanged }) {
  const bestValue = (bid.tags || []).includes("best_value");
  // Shared star-row renderer (0.5-step rounding).
  const Stars = ({ value }) => {
    const v = Math.max(0, Math.min(5, Number(value) || 0));
    return (
      <span style={{display:"inline-flex",gap:1,verticalAlign:"middle"}} aria-label={`${v} of 5`}>
        {[1,2,3,4,5].map(i => (
          <span key={i} style={{color: i <= Math.round(v) ? "#F59E0B" : "#D1D5DB", fontSize:11, lineHeight:1}}>★</span>
        ))}
      </span>
    );
  };
  // Anonymized buyer view: no supplier names, no production notes.
  // Added trust badges, quality signals (on-time %, jobs completed), and
  // renamed "Masked score" → "Match score" so the buyer feels the number is
  // for them, not something withheld from them.
  if (role === "buyer") {
    const badges = bid.trust_badges || [];
    return (
      <button
        data-testid={`bid-card-${bid.id}`}
        onClick={onSelect}
        className={`bid-card anonymous ${selected ? "selected" : ""}`}
        style={bestValue ? {boxShadow:"inset 3px 0 0 #10B981"} : undefined}
      >
        <div className="bid-rank">{index + 1}</div>
        <div className="bid-info">
          <b>{bid.anon_label} <span className="bid-badge"><Lock size={11}/> Sealed</span>{bid.is_verified ? <span style={{background:"#DCFCE7",color:"#065F46",padding:"1px 6px",borderRadius:999,fontSize:9,fontWeight:800,marginLeft:4,whiteSpace:"nowrap"}}>✓ Verified</span> : null}</b>
          <BidTags bid={bid}/>
          <BelowFloor bid={bid} job={job}/>
          <DeliveredIn bid={bid}/>
          <NeedByBadge bid={bid}/>
          <span className="bid-meta" data-testid={`bid-stars-${bid.id}`}>
            <MapPin size={11}/> {bid.location_radius_km} km · <Stars value={bid.quality_rating}/> {Number(bid.quality_rating || 0).toFixed(1)}{bid.rating_count > 0 ? <span style={{color:"#6B7280"}}> · {bid.rating_count} review{bid.rating_count === 1 ? "" : "s"}</span> : <span style={{color:"#9CA3AF"}}> · new</span>} · <Clock3 size={11}/> {bid.turnaround_days}d
          </span>
          {(bid.on_time_pct != null || bid.jobs_completed != null) && (
            <span data-testid={`bid-quality-signals-${bid.id}`} style={{fontSize:11,color:"#475569",display:"inline-flex",gap:8,marginTop:2}}>
              {bid.on_time_pct != null && <span>On-time <b>{Math.round(bid.on_time_pct)}%</b></span>}
              {bid.jobs_completed != null && <span>· <b>{bid.jobs_completed}</b> job{bid.jobs_completed === 1 ? "" : "s"} completed</span>}
            </span>
          )}
          {badges.length > 0 && (
            <span data-testid={`bid-trust-badges-${bid.id}`} style={{display:"inline-flex",flexWrap:"wrap",gap:4,marginTop:4}}>
              {badges.map(b => (
                <span key={b.key} style={{background:"#F0F9FF",color:"#075985",border:"1px solid #BAE6FD",padding:"1px 6px",borderRadius:999,fontSize:9,fontWeight:700,letterSpacing:"0.02em"}}>
                  ✓ {b.label}
                </span>
              ))}
            </span>
          )}
          <small className="score-bar">
            Match score <b>{bid.masked_score}</b>
            <span className="score-track"><span className="score-fill" style={{ width: `${Math.min(100, bid.masked_score)}%` }}/></span>
          </small>
        </div>
        <strong style={{textAlign:"right"}}>{money(bid.price)}<PerUnit price={bid.price} qty={job?.quantity} unit={job?.quantity_unit} testid={`bid-per-unit-${bid.id}`}/><LandedCost bid={bid} job={job}/></strong>
      </button>
    );
  }
  // Printer/admin view — includes production note & supplier name
  return (
    <div className="bid-card" data-testid={`bid-card-${bid.id}`} style={bestValue ? {boxShadow:"inset 3px 0 0 #10B981"} : undefined}>
      <div className="bid-rank">{index + 1}</div>
      <div className="bid-info">
        <b>{bid.supplier_name || bid.anon_label}</b>
        <BidTags bid={bid}/>
        <BelowFloor bid={bid} job={job}/>
        <span><Stars value={bid.quality_rating}/> {Number(bid.quality_rating || 0).toFixed(1)}{bid.rating_count ? ` · ${bid.rating_count} reviews` : ""} · {bid.turnaround_days}d · Score {bid.masked_score}</span>
        <DeliveredIn bid={bid}/>
        <NeedByBadge bid={bid}/>
        {bid.production_note && <small>{bid.production_note}</small>}
        {role === "admin" && (bid.revisions || []).length > 0 && <BidRevisionHistory bid={bid} job={job}/>}
        {role === "admin" && ["live", "closed"].includes(job?.status) && !job?.awarded_supplier_id && <AdminBidEditor bid={bid} job={job} onSaved={onChanged}/>}
      </div>
      <strong style={{textAlign:"right"}}>{money(bid.price)}<PerUnit price={bid.price} qty={job?.quantity} unit={job?.quantity_unit} testid={`bid-per-unit-${bid.id}`}/><LandedCost bid={bid} job={job}/></strong>
    </div>
  );
}

export function BriefDetail({ brief, refresh, close }) {
  const approve = async () => {
    await axios.post(`${API}/briefs/${brief.id}/proof/approve`, { approved: true, feedback: "Approved from workspace" });
    await refresh();
    close();
  };
  return (
    <>
      <div className="detail-block">
        <label>Brand</label><b>{brief.brand_name}</b>
        <label>Creative direction</label><p>{brief.style}</p>
        <label>Production size</label><p>{brief.dimensions} · {brief.substrate}</p>
        {brief.notes && <><label>Notes</label><p>{brief.notes}</p></>}
      </div>
      <div className="drawer-callout">
        <Sparkles size={17}/>
        <div><b>Agency matching in progress</b><span>3 vetted agencies are reviewing this brief.</span></div>
      </div>
      <button data-testid="brief-approve-proof" className="primary-btn full" onClick={approve}>
        Approve 3D soft-proof <CheckCircle2 size={16}/>
      </button>
    </>
  );
}

export function AdminReview({ job, refresh, close }) {
  const rc = job.required_capabilities || {};
  const [form, setForm] = useState({
    title: job.title || "",
    category: job.category || "Packaging",
    quantity: job.quantity || 1,
    dimensions: job.dimensions || "",
    stock: job.stock || "",
    finishes: job.finishes || "None",
    ship_to: job.ship_to || "",
    bid_window: job.bid_window || 12,
    price_floor: job.price_floor || 0,
    capability_tags: (job.capability_tags || []).join(", "),
    press_type: rc.press_type || "",
    substrate: rc.substrate || "",
    post_press: (rc.post_press || []).join(", "),
    product_type: rc.product_type || "",
    buyer_city: rc.buyer_city || "",
    buyer_state: rc.buyer_state || "",
    max_distance_km: rc.max_distance_km || 500,
  });
  const [ref, setRef] = useState({ press_types: [], post_press: [], product_capabilities: [] });
  useEffect(() => { axios.get(`${API}/capabilities/reference`).then(r => setRef(r.data)); }, []);
  const save = async () => {
    const required_capabilities = {
      press_type: form.press_type || null,
      substrate: form.substrate || null,
      post_press: form.post_press.split(",").map(s => s.trim()).filter(Boolean),
      product_type: form.product_type || null,
      quantity: Number(form.quantity),
      buyer_city: form.buyer_city || null,
      buyer_state: form.buyer_state || null,
      max_distance_km: Number(form.max_distance_km) || null,
    };
    await axios.patch(`${API}/jobs/${job.id}/approve`, {
      title: form.title, category: form.category, quantity: Number(form.quantity),
      dimensions: form.dimensions, stock: form.stock, finishes: form.finishes, ship_to: form.ship_to,
      bid_window: Number(form.bid_window),
      price_floor: Number(form.price_floor),
      capability_tags: form.capability_tags.split(",").map(s => s.trim()).filter(Boolean),
      required_capabilities,
    });
    await refresh();
    close();
  };
  return (
    <div className="review-box">
      <div className="review-note">
        <ShieldCheck size={17}/>
        <span>Admin gate: this request stays private until you release it. Set a price floor to reject unrealistic lowballs.</span>
      </div>
      <label>Title
        <input data-testid="review-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}/>
      </label>
      <label>Ship to
        <input data-testid="review-ship-to-input" value={form.ship_to} onChange={e => setForm({ ...form, ship_to: e.target.value })}/>
      </label>
      <label>Stock
        <input data-testid="review-stock-input" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })}/>
      </label>
      <label>Finishes
        <input data-testid="review-finishes-input" value={form.finishes} onChange={e => setForm({ ...form, finishes: e.target.value })}/>
      </label>
      <label>Dimensions
        <input data-testid="review-dimensions-input" value={form.dimensions} onChange={e => setForm({ ...form, dimensions: e.target.value })}/>
      </label>
      <label>Price floor (INR)
        <input data-testid="review-price-floor-input" type="number" value={form.price_floor} onChange={e => setForm({ ...form, price_floor: e.target.value })}/>
      </label>
      <label>Legacy capability tags (comma-separated)
        <input data-testid="review-capability-input" value={form.capability_tags} onChange={e => setForm({ ...form, capability_tags: e.target.value })} placeholder="Kraft substrate, Spot varnish"/>
      </label>

      <div className="review-caps-block" data-testid="review-required-caps">
        <b>5-Pillar Capability Requirements</b>
        <div className="req-caps-grid">
          <label>Press type
            <select data-testid="review-press-type" value={form.press_type} onChange={e => setForm({ ...form, press_type: e.target.value })}>
              <option value="">Any</option>
              {ref.press_types.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label>Product type (hard gate)
            <select data-testid="review-product-type" value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })}>
              <option value="">Any</option>
              {(ref.product_capabilities || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label>Substrate
            <input data-testid="review-substrate" value={form.substrate} onChange={e => setForm({ ...form, substrate: e.target.value })} placeholder="Kraft 120gsm"/>
          </label>
          <label>Required finishes
            <input data-testid="review-post-press" value={form.post_press} onChange={e => setForm({ ...form, post_press: e.target.value })} placeholder="Spot UV, Die-cut"/>
          </label>
          <label>Buyer city
            <input data-testid="review-buyer-city" value={form.buyer_city} onChange={e => setForm({ ...form, buyer_city: e.target.value })}/>
          </label>
          <label>Buyer state
            <input data-testid="review-buyer-state" value={form.buyer_state} onChange={e => setForm({ ...form, buyer_state: e.target.value })}/>
          </label>
          <label>Max distance (km)
            <input data-testid="review-max-distance" type="number" value={form.max_distance_km} onChange={e => setForm({ ...form, max_distance_km: e.target.value })}/>
          </label>
        </div>
      </div>

      <button data-testid="approve-release-button" className="primary-btn full" onClick={save}>
        Approve, set floor & release to matched suppliers <Gavel size={16}/>
      </button>
    </div>
  );
}
